import { getCategoryRankingPointsTable } from '@/api/category-ranking/categoryRankingApi';
import type { CategoryRankingPointsRule } from '@/api/category-ranking/types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { getCategoryRankingStageLabel } from '@/utils/category-ranking-utils';
import { HelpCircle, LoaderCircle } from 'lucide-react';
import type React from 'react';
import { useEffect, useState } from 'react';

export const CategoryRankingPointsInfoModal: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [rules, setRules] = useState<CategoryRankingPointsRule[] | null>(null);
  const [failed, setFailed] = useState(false);

  // Fetched on first open and cached while the panel stays mounted: it's 8 SSM reads on
  // the backend side, not worth requesting on every open. A failure leaves `rules` null
  // so the next open retries instead of showing the error forever.
  useEffect(() => {
    if (!isOpen || rules) return;
    let cancelled = false;
    setFailed(false);
    getCategoryRankingPointsTable()
      .then((data) => {
        if (!cancelled) setRules(data ?? []);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, rules]);

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" className="flex items-center gap-2">
          <HelpCircle size={16} />
          ¿Cómo se suman los puntos?
        </Button>
      </DialogTrigger>
      <DialogContent className="mx-auto max-h-[85vh] overflow-y-auto p-6 sm:max-w-[520px]">
        <DialogHeader>
          <DialogTitle>Cómo se suman los puntos</DialogTitle>
          <DialogDescription>
            Cada jugador suma puntos según la instancia que alcanza en cada
            torneo del club.
          </DialogDescription>
        </DialogHeader>

        {failed ? (
          <p className="py-4 text-sm text-gray-500">
            No se pudo cargar la tabla de puntos. Volvé a abrir esta ventana para
            reintentar.
          </p>
        ) : rules === null ? (
          <div className="flex justify-center py-8">
            <LoaderCircle className="h-6 w-6 animate-spin text-blue-600" />
          </div>
        ) : rules.length === 0 ? (
          <p className="py-4 text-sm text-gray-500">
            Todavía no hay una tabla de puntos configurada.
          </p>
        ) : (
          <div className="mt-2 overflow-hidden rounded-lg border border-gray-200">
            {rules.map((rule, index) => (
              <div
                key={rule.stage}
                className={`flex items-center justify-between px-4 py-2 ${
                  index > 0 ? 'border-t border-gray-200' : ''
                }`}
              >
                <span className="text-sm text-gray-700">
                  {getCategoryRankingStageLabel(rule.stage)}
                </span>
                <span className="text-sm font-bold">{rule.points} pts</span>
              </div>
            ))}
          </div>
        )}

        <ul className="mt-4 list-disc space-y-2 pl-5 text-sm text-gray-600">
          <li>
            Todo equipo inscripto que no llega al cuadro final suma igual los
            puntos de la instancia más baja.
          </li>
          <li>
            El campeón suma un valor fijo, no proporcional al tamaño del cuadro.
          </li>
          <li>
            Sólo suman los torneos que tengan activo &ldquo;Suma al ranking de
            categoría&rdquo;.
          </li>
          <li>
            Cada torneo define a qué ranking suma: al de la categoría{' '}
            <strong>de cada jugador</strong> al momento del torneo, o todo a una{' '}
            <strong>categoría fija</strong> que elige quien lo crea.
          </li>
          <li>
            Por eso un ranking de categoría muestra{' '}
            <strong>los puntos ganados como esa categoría</strong>, y no los
            jugadores que hoy pertenecen a ella: si alguien subió o bajó de
            categoría, sus puntos viejos siguen donde los ganó.
          </li>
          <li>
            Este ranking es independiente del ELO, que se calcula siempre.
          </li>
          <li>
            Un club puede <strong>migrar el ranking</strong> que llevaba antes
            de GetMatch: esos puntos valen lo mismo que los de la tabla,
            aparecen como <strong>Migrado</strong> en el detalle del jugador y
            solo cuentan para el ranking de ese club, no para el ranking
            general de GetMatch.
          </li>
        </ul>
      </DialogContent>
    </Dialog>
  );
};
